/**
 * Assessable income for the residential aged care means test (the MTA behind the hotelling and
 * non-clinical care contributions).
 *
 * THE RULE, per the Aged Care Act 2024 arrangements: ordinary income as assessed for the Age
 * Pension PLUS the person's income support payment, EXCLUDING the minimum pension supplement and
 * the energy supplement.
 *
 *   - DVA CLIK 4.3 ("What is total assessable income"): total assessable income "includes a
 *     resident's income support payment (excluding the minimum pension supplement and energy
 *     supplement), and their ordinary/adjusted income as per pension assessment rules".
 *   - DoHAC, "Means assessment for residential aged care" (1 Nov 2025 arrangements): assessable
 *     income "includes the person's social security payment (such as their Age Pension or Service
 *     Pension) but excludes the Energy Supplement and minimum pension supplement".
 *   - The income free area ($35,313.20 at 20 Mar 2026) is CONSTRUCTED on this basis — it sits just
 *     above the maximum single pension precisely so that a full pensioner with little else pays no
 *     income-tested amount. Testing pension-exclusive income against it hands every client an
 *     extra ~$31k of headroom that the Schedule never intended.
 *
 * THE DEFECT THIS REPLACES (adviser-verified against a live client, 2026-08-21): the engine fed
 * the means test `totalAssessableIncome` — the Age Pension income-test measure, which by
 * definition excludes the pension itself. For a single part-pensioner in care the income-tested
 * amount came out $0 and the NCCC was understated by ~$11/day (~$3,900/yr). The couple branch was
 * even written as though the pension were included (subtract it, halve, add half back — which
 * cancels), so the intent existed; the data feed didn't match it.
 *
 * Rate note: each member of an illness-separated couple receives SINGLE-rate supplements, and a
 * couple with someone in residential care is illness-separated — the caller passes that flag from
 * `isIllnessSeparatedCouple` / its strategy context rather than this module re-deriving it.
 */
import { MINIMUM_PENSION_SUPPLEMENT, ENERGY_SUPPLEMENT_PENSION } from './constants/ratesAndThresholds.ts';

export interface AgedCareIncomeParts {
    /** Ordinary assessable income per the pension income test (deemed + other income), EXCLUDING the pension itself. */
    totalAssessableIncome: number;
    /** Income support actually paid (household total for a couple). */
    socialSecurity: number;
}

/** The supplements excluded from one person's counted income support, per the rate that applies to them. */
function perPersonExclusion(singleRates: boolean): number {
    return singleRates
        ? MINIMUM_PENSION_SUPPLEMENT.single + ENERGY_SUPPLEMENT_PENSION.single
        : MINIMUM_PENSION_SUPPLEMENT.coupleEach + ENERGY_SUPPLEMENT_PENSION.coupleEach;
}

/**
 * @returns the aged-care assessable income for the means test — PER PERSON (a couple's combined
 * means are halved, per the Act's 50% attribution).
 */
export function agedCareAssessableIncomeOf(
    financials: AgedCareIncomeParts,
    options: { isCouple: boolean; illnessSeparated?: boolean },
): number {
    const ordinary = financials.totalAssessableIncome || 0;
    const support = Math.max(0, financials.socialSecurity || 0);

    if (!options.isCouple) {
        const includable = Math.max(0, support - perPersonExclusion(true));
        return ordinary + includable;
    }

    // Illness-separated members are paid — and shed — SINGLE-rate supplements. `socialSecurity` is
    // the household total; both members of a pensioner couple ordinarily receive the pension, so
    // two exclusions apply, floored at what is actually paid (a tiny part-pension cannot go
    // negative). Where only one member is on payment this excludes slightly too much, which errs
    // toward a marginally HIGHER counted income and fee — the conservative side for advice.
    const eachRate = options.illnessSeparated !== false;
    const includable = Math.max(0, support - 2 * perPersonExclusion(eachRate));
    return (ordinary + includable) / 2;
}
